import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { api, type ThemeRow } from '../lib/api';

/**
 * Champ thèmes optionnel pour la capture : autocomplétion sur les thèmes
 * EXISTANTS (réutilise au lieu de dupliquer), ou saisie libre. Les thèmes
 * choisis sont passés à la capture (CAP-03 : fusionnés aux suggestions de l'IA).
 */
export function ThemePicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [sugg, setSugg] = useState<ThemeRow[]>([]);

  useEffect(() => {
    if (!q.trim()) {
      setSugg([]);
      return;
    }
    const id = setTimeout(() => {
      void api.browse
        .themes(q)
        .then((r) => setSugg(r.filter((x) => !value.includes(x.label)).slice(0, 6)))
        .catch(() => {});
    }, 180);
    return () => clearTimeout(id);
  }, [q, value]);

  const add = (label: string): void => {
    const l = label.trim();
    if (l && !value.includes(l)) onChange([...value, l]);
    setQ('');
    setSugg([]);
  };
  const remove = (label: string): void => onChange(value.filter((x) => x !== label));

  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && q.trim()) {
      e.preventDefault();
      add(q);
    } else if (e.key === 'Backspace' && !q && value.length) {
      remove(value[value.length - 1]!);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {value.map((th) => (
          <span key={th} className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-xs text-accent">
            # {th}
            <button onClick={() => remove(th)} aria-label={t('captureForm.removeTheme')} className="hover:opacity-70">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder={value.length ? '' : t('captureForm.themePlaceholder')}
          className="min-w-[8rem] flex-1 bg-transparent text-xs outline-none placeholder:text-muted"
        />
      </div>
      {sugg.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {sugg.map((s) => (
            <button
              key={s.id}
              onClick={() => add(s.label)}
              className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-muted hover:bg-accent hover:text-accent-fg"
            >
              # {s.label} <span className="opacity-60">· {s.n}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
